import { Pipe, PipeTransform } from '@angular/core';

/**
 * cents (integer) → "X,XXX.XX" the `(cents / 100).toFixed(2)` hand-rolled 8
 * times across admin and billing pages.
 *
 * Without a currency it returns the bare number, and the call site renders
 * `{{ currency | uppercase }}` alongside it (the admin tables still do). With
 * one - `{{ cents | money: currency }}` - it returns the symbol form ("€42.50",
 * "-€3.20"), which is what the billing pages use so amounts read the same way
 * everywhere a customer sees their own money.
 *
 * Grouped with a thousands separator so amounts line up with the other
 * separated numbers in the same tables (latency, request counts).
 */
@Pipe({
  name: 'money',
  standalone: false,
})
export class MoneyPipe implements PipeTransform {
  transform(cents: number | null | undefined, currency?: string | null): string {
    const value = (cents ?? 0) / 100;
    if (currency) {
      try {
        return value.toLocaleString('en-US', { style: 'currency', currency: currency.toUpperCase() });
      } catch {
        // Unknown ISO code - fall through to "42.50 XYZ" rather than throwing in a template.
        return `${MoneyPipe.plain(value)} ${currency.toUpperCase()}`;
      }
    }
    return MoneyPipe.plain(value);
  }

  private static plain(value: number): string {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
